import * as Blockly from 'blockly/core';
import { getAllBlockMeta, getBlockMeta, type BlockMeta, type FieldKind } from '../registry';
import { generateCpp, type Diagnostic, type PinUsage } from '../codegen/cpp';
import { buildSnapshot, type CanvasSnapshot } from './snapshot';

/**
 * Function-calling tools the Gemini assistant can invoke against the live
 * workspace. Execution is pure and headless-testable: every tool takes the
 * workspace and plain JSON arguments, and returns a structured result.
 */

export interface AiToolResult {
  ok: boolean;
  message: string;
}

export interface GeminiFunctionDeclaration {
  name: string;
  description: string;
  parameters: {
    type: 'OBJECT';
    properties: Record<string, { type: string; description: string; items?: { type: string } }>;
    required: string[];
  };
}

export const AI_TOOL_DECLARATIONS: GeminiFunctionDeclaration[] = [
  {
    name: 'get_canvas_state',
    description:
      'Read the current block canvas: all blocks with types, fields, nesting, positions, and variable definitions. Call this before modifying anything.',
    parameters: { type: 'OBJECT', properties: {}, required: [] },
  },
  {
    name: 'list_block_types',
    description:
      'List every available block type with its fields and legal values. Use this to discover valid block types and field options before calling add_block or update_block.',
    parameters: {
      type: 'OBJECT',
      properties: {
        category: {
          type: 'STRING',
          description:
            'Optional filter: control, gpio, analog, interrupts, time, power, comms, network, logic, math, variables, text.',
        },
      },
      required: [],
    },
  },
  {
    name: 'add_block',
    description:
      'Create a block on the canvas. Without parentId the block is placed top-level at x/y. With parentId+input the block is appended at the END of that statement input chain. Returns the new block id.',
    parameters: {
      type: 'OBJECT',
      properties: {
        type: { type: 'STRING', description: 'Block type, e.g. esp32_digital_write. Must exist in list_block_types.' },
        fields: { type: 'STRING', description: 'Optional JSON object of field name to value, e.g. {"PIN":"2","LEVEL":"HIGH"}.' },
        parentId: { type: 'STRING', description: 'Optional id of a parent block to attach under.' },
        input: { type: 'STRING', description: 'Statement input name on the parent, e.g. "DO", "DO0", "ELSE".' },
        x: { type: 'NUMBER', description: 'Optional canvas x for top-level blocks.' },
        y: { type: 'NUMBER', description: 'Optional canvas y for top-level blocks.' },
      },
      required: ['type'],
    },
  },
  {
    name: 'update_block',
    description: 'Set field values on an existing block (dropdowns, numbers, text, checkboxes).',
    parameters: {
      type: 'OBJECT',
      properties: {
        id: { type: 'STRING', description: 'Block id from get_canvas_state.' },
        fields: { type: 'STRING', description: 'JSON object of field name to new value.' },
      },
      required: ['id', 'fields'],
    },
  },
  {
    name: 'connect_blocks',
    description: 'Move an existing statement block to the end of a parent statement input chain.',
    parameters: {
      type: 'OBJECT',
      properties: {
        parentId: { type: 'STRING', description: 'Parent block id.' },
        input: { type: 'STRING', description: 'Statement input name on the parent.' },
        childId: { type: 'STRING', description: 'Block to move into the chain.' },
      },
      required: ['parentId', 'input', 'childId'],
    },
  },
  {
    name: 'delete_block',
    description: 'Delete a block and everything connected below it.',
    parameters: {
      type: 'OBJECT',
      properties: { id: { type: 'STRING', description: 'Block id.' } },
      required: ['id'],
    },
  },
  {
    name: 'clear_canvas',
    description: 'Remove all blocks from the canvas. Only use when the user clearly wants to start over.',
    parameters: { type: 'OBJECT', properties: {}, required: [] },
  },
  {
    name: 'get_diagnostics',
    description:
      'Run the static checker and a generator dry-run: pin conflicts, wiring problems, and warnings about the current program.',
    parameters: { type: 'OBJECT', properties: {}, required: [] },
  },
];

/* ------------------------------------------------------------------ */

function describeFieldKind(kind: FieldKind): Record<string, unknown> {
  switch (kind.kind) {
    case 'dropdown':
      return { kind: 'dropdown', options: kind.options };
    case 'value':
      return { kind: 'value', accepts: kind.types };
    case 'number':
      return { kind: 'number' };
    case 'boolean':
      return { kind: 'boolean' };
    default:
      return { kind: kind.kind };
  }
}

export function describeBlockTypes(category?: string): Record<string, unknown>[] {
  return getAllBlockMeta()
    .filter((m) => !category || m.category === category)
    .map((m) => ({
      type: m.type,
      category: m.category,
      label: m.label,
      description: m.description,
      hat: m.hat ?? false,
      statementInput: m.statementInput ?? null,
      fields: Object.fromEntries(Object.entries(m.fields).map(([k, v]) => [k, describeFieldKind(v)])),
    }));
}

function jsonField(args: Record<string, unknown>, name: string): Record<string, unknown> | null {
  const raw = args[name];
  if (raw === undefined || raw === null || raw === '') return null;
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  try {
    return JSON.parse(String(raw)) as Record<string, unknown>;
  } catch {
    throw new Error(`Field "${name}" must be a JSON object.`);
  }
}

function findFreeStatementConnection(parent: Blockly.Block, inputName: string): Blockly.Connection | null {
  const input = parent.getInput(inputName);
  if (!input || !input.connection) return null;
  let conn = input.connection;
  while (conn.targetBlock()) {
    const next = conn.targetBlock()!.nextConnection;
    if (!next) return null;
    conn = next;
  }
  return conn;
}

function validateFields(block: Blockly.Block, meta: BlockMeta, fields: Record<string, unknown>): string[] {
  const applied: string[] = [];
  for (const [name, raw] of Object.entries(fields)) {
    const kind = meta.fields[name];
    if (!kind) {
      throw new Error(`Block "${meta.type}" has no field "${name}". Legal fields: ${Object.keys(meta.fields).join(', ') || 'none'}.`);
    }
    if (kind.kind === 'dropdown') {
      const value = String(raw);
      if (!kind.options.includes(value)) {
        throw new Error(`Field "${name}" must be one of: ${kind.options.join(', ')} (got "${value}").`);
      }
      block.setFieldValue(value, name);
    } else if (kind.kind === 'boolean') {
      const value = typeof raw === 'boolean' ? (raw ? 'TRUE' : 'FALSE') : String(raw).toUpperCase();
      block.setFieldValue(value === 'TRUE' || value === '1', name);
    } else if (kind.kind === 'number') {
      const value = Number(raw);
      if (!Number.isFinite(value)) throw new Error(`Field "${name}" must be a number.`);
      block.setFieldValue(value, name);
    } else {
      block.setFieldValue(String(raw), name);
    }
    applied.push(name);
  }
  return applied;
}

/* ------------------------------------------------------------------ */

export class AiToolExecutor {
  constructor(private workspace: Blockly.Workspace) {}

  execute(name: string, args: Record<string, unknown>): AiToolResult {
    try {
      switch (name) {
        case 'get_canvas_state': {
          const snapshot: CanvasSnapshot = buildSnapshot(this.workspace);
          return { ok: true, message: JSON.stringify(snapshot) };
        }
        case 'list_block_types': {
          const category = typeof args['category'] === 'string' ? args['category'] : undefined;
          return { ok: true, message: JSON.stringify(describeBlockTypes(category)) };
        }
        case 'add_block':
          return this.addBlock(args);
        case 'update_block':
          return this.updateBlock(args);
        case 'connect_blocks':
          return this.connectBlocks(args);
        case 'delete_block':
          return this.deleteBlock(args);
        case 'clear_canvas':
          this.workspace.clear();
          return { ok: true, message: 'Canvas cleared.' };
        case 'get_diagnostics':
          return this.diagnostics();
        default:
          return { ok: false, message: `Unknown tool "${name}".` };
      }
    } catch (err) {
      return { ok: false, message: err instanceof Error ? err.message : String(err) };
    }
  }

  private addBlock(args: Record<string, unknown>): AiToolResult {
    const type = String(args['type'] ?? '');
    const meta = getBlockMeta(type);
    if (!meta) {
      const known = getAllBlockMeta().map((m) => m.type);
      return {
        ok: false,
        message: `Unknown block type "${type}". Valid types: ${known.join(', ')}`,
      };
    }
    const fields = jsonField(args, 'fields') ?? {};
    const json: Record<string, unknown> = { type, fields: {} };
    if (Object.keys(fields).length) json['fields'] = fields;
    if (typeof args['x'] === 'number' && typeof args['y'] === 'number') {
      json['x'] = args['x'];
      json['y'] = args['y'];
    }
    // v11's append return value is unreliable across builds; pin an explicit id.
    const blockId = typeof json['id'] === 'string' ? (json['id'] as string) : Blockly.utils.idGenerator.genUid();
    json['id'] = blockId;

    Blockly.serialization.blocks.append(json as never, this.workspace, {
      recordUndo: true,
    });
    const block = this.workspace.getBlockById(blockId);
    if (!block) {
      return { ok: false, message: `Could not create block "${type}".` };
    }
    if (Object.keys(fields).length) validateFields(block, meta, fields);

    const parentId = typeof args['parentId'] === 'string' ? args['parentId'] : '';
    const inputName = typeof args['input'] === 'string' ? args['input'] : '';
    if (parentId) {
      const parent = this.workspace.getBlockById(parentId);
      if (!parent) return { ok: false, message: `Parent block "${parentId}" not found.` };
      const input = parent.getInput(inputName);
      if (!input || !input.connection) {
        const statementInputs = parent.inputList
          .filter((i) => i.connection && i.connection.type === Blockly.ConnectionType.NEXT_STATEMENT)
          .map((i) => i.name);
        return {
          ok: false,
          message: `Block "${parent.type}" has no statement input "${inputName}". Its statement inputs: ${statementInputs.join(', ') || 'none'}.`,
        };
      }
      if (!block.previousConnection) {
        return { ok: false, message: `Block "${type}" cannot be attached to a statement chain (it is a value or hat block).` };
      }
      const conn = findFreeStatementConnection(parent, inputName);
      if (!conn) {
        return { ok: false, message: `Input "${inputName}" is not chainable (its last block has no next connection).` };
      }
      block.previousConnection.connect(conn);
    }
    return { ok: true, message: `Created ${type} with id ${block.id}.` };
  }

  private updateBlock(args: Record<string, unknown>): AiToolResult {
    const id = String(args['id'] ?? '');
    const block = this.workspace.getBlockById(id);
    if (!block) return { ok: false, message: `Block "${id}" not found.` };
    const meta = getBlockMeta(block.type);
    const fields = jsonField(args, 'fields');
    if (!fields) return { ok: false, message: 'Provide a "fields" JSON object.' };
    if (!meta) {
      for (const [name, value] of Object.entries(fields)) block.setFieldValue(value as never, name);
      return { ok: true, message: `Updated ${block.type}.` };
    }
    const applied = validateFields(block, meta, fields);
    return { ok: true, message: `Updated fields [${applied.join(', ')}] on ${block.type} (${id}).` };
  }

  private connectBlocks(args: Record<string, unknown>): AiToolResult {
    const parentId = String(args['parentId'] ?? '');
    const childId = String(args['childId'] ?? '');
    const inputName = String(args['input'] ?? '');
    const parent = this.workspace.getBlockById(parentId);
    const child = this.workspace.getBlockById(childId);
    if (!parent) return { ok: false, message: `Parent block "${parentId}" not found.` };
    if (!child) return { ok: false, message: `Block "${childId}" not found.` };
    if (!child.previousConnection) {
      return { ok: false, message: `Block "${child.type}" has no previous connection and cannot be chained.` };
    }
    const conn = findFreeStatementConnection(parent, inputName);
    if (!conn) {
      return { ok: false, message: `No free connection at "${inputName}" on ${parent.type}.` };
    }
    child.previousConnection.connect(conn);
    return { ok: true, message: `Moved ${child.type} into ${parent.type}.${inputName}.` };
  }

  private deleteBlock(args: Record<string, unknown>): AiToolResult {
    const id = String(args['id'] ?? '');
    const block = this.workspace.getBlockById(id);
    if (!block) return { ok: false, message: `Block "${id}" not found.` };
    const type = block.type;
    block.dispose(true);
    return { ok: true, message: `Deleted ${type} (${id}) and its children.` };
  }

  private diagnostics(): AiToolResult {
    const result = generateCpp(this.workspace);
    const pins: PinUsage[] = result.pins;
    const errors = result.diagnostics.filter((d: Diagnostic) => d.level === 'error');
    return {
      ok: true,
      message: JSON.stringify({
        diagnostics: result.diagnostics,
        errors: errors.length,
        pinUsage: pins,
        libraries: result.libraries,
      }),
    };
  }
}
